import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { HerracContext } from "../../App";
import './SubcategoriesForm.css';

function WaterTransportForm({ additionalAttributes, setAdditionalAttributes, subCategoryId }) {
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState("");
  const { fuelTypeData } = useContext(HerracContext);

  // Debug logging
  useEffect(() => {
    console.log('⛵ WaterTransportForm mounted with subCategoryId:', subCategoryId);
    console.log('📋 Current additionalAttributes:', additionalAttributes);
  }, [subCategoryId, additionalAttributes]);

  useEffect(() => {
    if (subCategoryId) {
      console.log('📦 Fetching brands for water transport...');
      axios.get(`https://api.herracim.az/api/Brand/BySubCategoryId?subCategoryId=${subCategoryId}`)
        .then(res => {
          console.log('✅ Brands loaded:', res.data.length, 'items');
          setFilteredBrands(res.data);
          setFilteredModels([]);
        })
        .catch(err => {
          console.error('❌ Error loading brands:', err);
          setFilteredBrands([]);
        });
    } else {
      setFilteredBrands([]);
      setFilteredModels([]);
    }
  }, [subCategoryId]);

  useEffect(() => {
    if (selectedBrand) {
      console.log('📦 Fetching models for brand:', selectedBrand);
      axios.get(`https://api.herracim.az/api/Model/ByBrandId?brandId=${selectedBrand}`)
        .then(res => {
          console.log('✅ Models loaded:', res.data.length, 'items');
          setFilteredModels(res.data);
        })
        .catch(err => {
          console.error('❌ Error loading models:', err);
          setFilteredModels([]);
        });
    } else {
      setFilteredModels([]);
    }
  }, [selectedBrand]);

  const handleBrandChange = (e) => {
    const value = e.target.value;
    console.log('🏷️ Brand selected:', value);
    
    setSelectedBrand(value);
    setAdditionalAttributes({
      ...additionalAttributes,
      brandId: value,
      modelId: ""
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    console.log(`🔧 Field changed: ${name} = ${value}`);
    
    setAdditionalAttributes({
      ...additionalAttributes,
      [name]: type === "checkbox" ? checked : value
    });
  };

  const years = Array.from({ length: new Date().getFullYear() - 1979 }, (_, i) => 1980 + i).reverse();

  const usageRegions = [
    "Xəzər dənizi",
    "Böyük su anbarları (Mingəçevir, Şəmkir və s.)",
    "Daxili göllər (Göygöl, Nohur, Ağgöl və s.)",
    "Çaylar (Kür, Araz və s.)",
    "Dəniz sahil zonası",
    "Turistik sahələr (Qəbələ, İsmayıllı və s.)",
    "İcarə məqsədli liman bölgələri",
    "Xarici dənizlərdə istifadə (Avropa, Asiya bazarı)",
    "Qapalı hovuz və marina istifadəsi",
    "Balıqçılıq sahələri",
    "Turist gəzinti zonaları",
    "Şəxsi istirahət zonası"
  ];

  return (
    <div className="waterTransportForm">
      <div>
        <h3>
          Marka <span>*</span>
          <span className="tooltip-icon" title="Gəmi/Qayıq istehsalçısı">ℹ️</span>
        </h3>
        <select 
          name="brandId" 
          onChange={handleBrandChange} 
          disabled={filteredBrands.length === 0}
          defaultValue=""
        >
          <option value="" disabled>
            {filteredBrands.length === 0 ? 'Yüklənir...' : 'Seçin'}
          </option>
          {filteredBrands.map(brand => (
            <option key={brand.id} value={brand.id}>{brand.name}</option>
          ))}
        </select>
      </div>

      <div>
        <h3>
          Model <span>*</span>
          <span className="tooltip-icon" title="Gəmi/Qayıq modeli">ℹ️</span>
        </h3>
        <select 
          name="modelId" 
          onChange={handleChange} 
          disabled={filteredModels.length === 0}
          defaultValue=""
        >
          <option value="" disabled>
            {selectedBrand ? (filteredModels.length === 0 ? 'Yüklənir...' : 'Seçin') : 'Əvvəlcə marka seçin'}
          </option>
          {filteredModels.map(model => (
            <option key={model.id} value={model.id}>{model.name}</option>
          ))}
        </select>
      </div>

      <div>
        <h3>
          İstehsal ili <span>*</span>
          <span className="tooltip-icon" title="Buraxılış ili">ℹ️</span>
        </h3>
        <select name="year" onChange={handleChange} defaultValue="" required>
          <option value="" disabled>Seçin</option>
          {years.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      <div>
        <h3>
          Yanacaq növü <span>*</span>
          <span className="tooltip-icon" title="Mühərrik yanacağı">ℹ️</span>
        </h3>
        <select name="fuelTypeId" onChange={handleChange} defaultValue="" required>
          <option value="" disabled>Seçin</option>
          {fuelTypeData.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </div>

      <div>
        <h3>
          Mühərrik gücü (HP) <span>*</span>
          <span className="tooltip-icon" title="Horsepower">ℹ️</span>
        </h3>
        <input 
          type="number" 
          name="horsePower" 
          min="1" 
          placeholder="150"
          onChange={handleChange} 
          required
        />
      </div>

      <div>
        <h3>
          Uzunluq (m) <span>*</span>
          <span className="tooltip-icon" title="Gəminin uzunluğu">ℹ️</span>
        </h3>
        <input 
          type="number" 
          name="length" 
          step="0.1" 
          min="1" 
          placeholder="8.5"
          onChange={handleChange} 
          required
        />
      </div>

      <div>
        <h3>
          Sürət (km/saat) <span>*</span>
          <span className="tooltip-icon" title="Maksimum sürət">ℹ️</span>
        </h3>
        <input 
          type="number" 
          name="speed" 
          min="1" 
          placeholder="45"
          onChange={handleChange} 
          required
        />
      </div>

      <div>
        <h3>
          İstifadə məqsədi <span>*</span>
          <span className="tooltip-icon" title="Gəminin təyinatı">ℹ️</span>
        </h3>
        <select name="usagePurpose" onChange={handleChange} defaultValue="" required>
          <option value="" disabled>Seçin</option>
          <option value="Şəxsi">Şəxsi istifadə</option>
          <option value="Balıqçılıq">Balıqçılıq</option>
          <option value="Səyahət">Səyahət / Turizm</option>
          <option value="İcarəyə verilir">İcarəyə verilir</option>
          <option value="Kommersiya">Kommersiya məqsədi</option>
        </select>
      </div>

      <div>
        <h3>
          Rəng <span>*</span>
          <span className="tooltip-icon" title="Korpus rəngi">ℹ️</span>
        </h3>
        <input 
          type="text" 
          name="color" 
          placeholder="Ağ, Mavi, Qırmızı..."
          onChange={handleChange} 
          required
        />
      </div>

      <div>
        <h3>
          Oturacaq sayı <span>*</span>
          <span className="tooltip-icon" title="Maksimum sərnişin tutumu">ℹ️</span>
        </h3>
        <select name="seatCount" onChange={handleChange} defaultValue="" required>
          <option value="" disabled>Seçin</option>
          <option value="2">2-4 nəfər</option>
          <option value="6">6-8 nəfər</option>
          <option value="10">10-12 nəfər</option>
          <option value="15">15+ nəfər</option>
        </select>
      </div>

      <div>
        <h3>
          Mühərrik tipi <span>*</span>
          <span className="tooltip-icon" title="Engine type">ℹ️</span>
        </h3>
        <select name="engineType" onChange={handleChange} defaultValue="" required>
          <option value="" disabled>Seçin</option>
          <option value="Daxili">Daxili mühərrik (Inboard)</option>
          <option value="Xarici">Xarici mühərrik (Outboard)</option>
          <option value="İki mühərrik">İki mühərrik</option>
          <option value="Yelkənli">Yelkənli</option>
        </select>
      </div>

      <div>
        <h3>
          Mühərrik həcmi (L) <span>*</span>
          <span className="tooltip-icon" title="Engine displacement">ℹ️</span>
        </h3>
        <input 
          type="number" 
          name="engineVolume" 
          step="0.1" 
          placeholder="3.0"
          min="0.5"
          max="15"
          onChange={handleChange} 
          required
        />
      </div>

      <div>
        <h3>
          Korpus materialı <span>*</span>
          <span className="tooltip-icon" title="Hull material">ℹ️</span>
        </h3>
        <select name="material" onChange={handleChange} defaultValue="" required>
          <option value="" disabled>Seçin</option>
          <option value="Fiberglas">Fiberglas (FRP)</option>
          <option value="Alüminium">Alüminium</option>
          <option value="Polad">Polad</option>
          <option value="Ağac">Ağac</option>
          <option value="PVC">PVC (Şişmə)</option>
          <option value="Kompozit">Kompozit</option>
        </select>
      </div>

      <div>
        <h3>
          Yeni? <span>*</span>
          <span className="tooltip-icon" title="0 saat yenidirmi?">ℹ️</span>
        </h3>
        <select name="isNew" onChange={handleChange} defaultValue="" required>
          <option value="" disabled>Seçin</option>
          <option value="true">Bəli (0 saat)</option>
          <option value="false">Xeyr (İşlənmiş)</option>
        </select>
      </div>

      <div>
        <h3>
          Qəzalı olub? <span>*</span>
          <span className="tooltip-icon" title="Qəza keçirib?">ℹ️</span>
        </h3>
        <select name="isAccidented" onChange={handleChange} defaultValue="" required>
          <option value="" disabled>Seçin</option>
          <option value="true">Bəli ⚠️</option>
          <option value="false">Xeyr ✅</option>
        </select>
      </div>

      <div>
        <h3>
          VIN nömrəsi <span>*</span>
          <span className="tooltip-icon" title="Hull Identification Number">ℹ️</span>
        </h3>
        <input 
          type="text" 
          name="vin" 
          placeholder="HIN nömrəsi"
          onChange={handleChange} 
          required
        />
      </div>

      <div>
        <h3>
          Bazar ölkəsi <span>*</span>
          <span className="tooltip-icon" title="Hansı bazar üçün istehsal olunub">ℹ️</span>
        </h3>
        <select name="marketCountry" onChange={handleChange} defaultValue="" required>
          <option value="" disabled>Seçin</option>
          <option value="Avropa">Avropa</option>
          <option value="Amerika">Amerika (ABŞ)</option>
          <option value="Türkiyə">Türkiyə</option>
          <option value="Çin">Çin</option>
          <option value="İtaliya">İtaliya</option>
          <option value="Yaponiya">Yaponiya</option>
          <option value="Digər">Digər</option>
        </select>
      </div>

      <div>
        <h3>
          İstifadə regionu <span>*</span>
          <span className="tooltip-icon" title="Harada istifadə olunur">ℹ️</span>
        </h3>
        <input 
          list="usageRegionList" 
          name="usageRegion" 
          placeholder="Region seçin və ya yazın"
          onChange={handleChange} 
          required
        />
        <datalist id="usageRegionList">
          {usageRegions.map((region, i) => (
            <option key={i} value={region} />
          ))}
        </datalist>
      </div>
    </div>
  );
}

export default WaterTransportForm;